import { TABLE_DELIMITER_CELL } from "./markdown-source-map";

export interface MarkdownTableLimits {
  readonly maxRows: number;
  readonly maxColumns: number;
}

export interface ParsedMarkdownTable {
  readonly cells: readonly (readonly string[])[];
  /** 行数または列数の上限で内容を切り詰めたか。 */
  readonly truncated: boolean;
}

interface ParsedRow {
  readonly cells: readonly string[];
  readonly columnCount: number;
  readonly hasSeparator: boolean;
  readonly isDelimiter: boolean;
}

/** Markdown のセル表現を、コピー時のエスケープ規則と逆順で復号する。 */
function decodeCell(raw: string): string {
  const value = raw.replace(/^[\t ]+|[\t ]+$/gu, "");
  let decoded = "";
  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]!;
    if (char === "\\" && (value[index + 1] === "\\" || value[index + 1] === "|")) {
      decoded += value[index + 1];
      index += 1;
      continue;
    }
    decoded += char;
  }
  return decoded;
}

/** 1 行を走査し、保持するセル数だけを構築する。 */
function parseRow(line: string, storedColumnLimit: number, delimiter: boolean): ParsedRow {
  const value = line.replace(/^[\t ]+|[\t ]+$/gu, "");
  const cells: string[] = [];
  let columnCount = 0;
  let cellStart = 0;
  let precedingBackslashes = 0;
  let hasSeparator = false;
  let lastSeparator = -1;
  let isDelimiter = true;

  const commitCell = (end: number): void => {
    const raw = value.slice(cellStart, end).replace(/^[\t ]+|[\t ]+$/gu, "");
    if (delimiter && !TABLE_DELIMITER_CELL.test(raw)) isDelimiter = false;
    if (cells.length < storedColumnLimit) cells.push(decodeCell(raw));
    columnCount += 1;
  };

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index]!;
    if (char === "\\") {
      precedingBackslashes += 1;
      continue;
    }
    if (char === "|" && precedingBackslashes % 2 === 0) {
      hasSeparator = true;
      lastSeparator = index;
      // 行頭のパイプは囲み記法であり、空の先頭セルにはしない。
      if (index === 0 && cellStart === 0 && columnCount === 0) {
        cellStart = 1;
      } else {
        commitCell(index);
        cellStart = index + 1;
      }
    }
    precedingBackslashes = 0;
  }

  // 行末の囲みパイプは空の末尾セルにはしない。
  if (lastSeparator !== value.length - 1) commitCell(value.length);
  return {
    cells,
    columnCount,
    hasSeparator,
    isDelimiter: delimiter && isDelimiter && columnCount > 0,
  };
}

/** 文字列の先頭・末尾にある空行だけを除く。 */
function trimBoundaryBlankLines(text: string): string {
  return text
    .replace(/^(?:[\t ]*(?:\r\n|\r|\n))+/u, "")
    .replace(/(?:\r\n|\r|\n)[\t ]*(?:(?:\r\n|\r|\n)[\t ]*)*$/u, "");
}

/** 改行形式を問わず、配列を全量構築せずに各行を走査する。 */
function* linesOf(text: string): Generator<string> {
  let start = 0;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    if (char !== "\r" && char !== "\n") continue;
    yield text.slice(start, index);
    if (char === "\r" && text[index + 1] === "\n") index += 1;
    start = index + 1;
  }
  yield text.slice(start);
}

/** 表図形を GFM の表テキストへ変換する（末尾に改行 1 つを付ける）。 */
export function tableObjectToMarkdown(cells: readonly (readonly string[])[]): string {
  const columnCount = Math.max(1, cells[0]?.length ?? 0);
  const encodeCell = (cell: string): string =>
    cell
      .replace(/\r\n|\r|\n/gu, " ")
      .replace(/\\/gu, "\\\\")
      .replace(/\|/gu, "\\|");
  const rowToMarkdown = (row: readonly string[]): string => {
    const encoded = new Array<string>(columnCount);
    for (let column = 0; column < columnCount; column += 1) {
      encoded[column] = encodeCell(row[column] ?? "");
    }
    return `| ${encoded.join(" | ")} |`;
  };

  const rows = cells.length > 0 ? cells : [[""]];
  return (
    // 連結しているのは HTML ではなく GFM の表の行。セル値は encodeCell() が改行・
    // バックスラッシュ・縦棒を escape 済みで、一致の原因は型引数 <string> の表記。
    // nosemgrep: tmp.security-work.sast.rules.javascript.browser.security.raw-html-join
    [
      rowToMarkdown(rows[0] ?? []),
      rowToMarkdown(new Array<string>(columnCount).fill("---")),
      ...rows.slice(1).map(rowToMarkdown),
    ].join("\n") + "\n"
  );
}

/**
 * GFM の表テキストを解析する。表として解釈できなければ null。
 * 解釈できた場合は、limits で切り詰めた長方形のセル配列を返す。
 */
export function parseMarkdownTable(
  text: string,
  limits: MarkdownTableLimits
): ParsedMarkdownTable | null {
  const maxRows = Number.isFinite(limits.maxRows) ? Math.max(1, Math.floor(limits.maxRows)) : 1;
  const maxColumns = Number.isFinite(limits.maxColumns)
    ? Math.max(1, Math.floor(limits.maxColumns))
    : 1;
  const iterator = linesOf(trimBoundaryBlankLines(text));
  const headerLine = iterator.next();
  const delimiterLine = iterator.next();
  if (headerLine.done || delimiterLine.done) return null;

  const header = parseRow(headerLine.value, maxColumns, false);
  const delimiter = parseRow(delimiterLine.value, 0, true);
  if (
    !header.hasSeparator ||
    header.columnCount < 1 ||
    !delimiter.hasSeparator ||
    !delimiter.isDelimiter ||
    delimiter.columnCount !== header.columnCount
  ) {
    return null;
  }

  const storedColumns = Math.min(header.columnCount, maxColumns);
  const rows: string[][] = [
    [...header.cells, ...new Array<string>(storedColumns - header.cells.length).fill("")],
  ];
  let truncated = header.columnCount > maxColumns;

  for (let next = iterator.next(); !next.done; next = iterator.next()) {
    const row = parseRow(next.value, storedColumns, false);
    // 前後へ普通の文章が混ざった入力は、表の一部だけを貼らない。
    if (!row.hasSeparator || row.columnCount < 1) return null;
    if (row.columnCount > header.columnCount) truncated = true;
    if (rows.length >= maxRows) {
      truncated = true;
      continue;
    }
    const cells = row.cells.slice(0, storedColumns);
    if (cells.length < storedColumns) {
      cells.push(...new Array<string>(storedColumns - cells.length).fill(""));
    }
    rows.push(cells);
  }

  return { cells: rows, truncated };
}
